import React, { useCallback, useRef } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import Logo from './Logo';
import NavigationLink from './NavigationLink';
import GET_CURRENT_USER from '../../shared/queries/currentUserQuery';
import PrivilegeNames from '../../../constants/privilegeNames';
import NavigationMenuCollapse from './NavigationMenuCollapse';
import useClickOutsideHandler from './utils/useClickOutsideHandler';
import { isMenuCollapsed } from '../../../apollo/cache';
import {
  getExecutiveSummaryUrl,
  getUnitSearchForAppraisalComparison,
  getUnitSearchForOrgReports
} from '../../shared/services/getUnitSearchPageUrlService';
import { getRoleAdjustedUrl } from '../../shared/services/getIndustryReportsPageUrlService';
import { Drawer } from '@mui/material';
import DownloadFeedbackReport from '../../feedback/components/DownloadFeedbackReport';

const NavigationMenu = () => {
  const menuCollapsed = useReactiveVar(isMenuCollapsed);
  const menuRef = useRef(null);

  const { data } = useQuery(GET_CURRENT_USER);

  const collapseMenu = useCallback(() => {
    isMenuCollapsed(true);
  }, []);

  useClickOutsideHandler(menuRef, collapseMenu);

  const userName = data?.currentUser?.FirstName || '';
  const userRoleId = data?.currentUser?.SystemRole?.Id || null;
  const userPrivileges = data?.currentUser?.Privileges || [];

  const hasAccessToWelcomePage = userPrivileges.includes(PrivilegeNames.WelcomePage);
  const hasAccessToIndustryReports = userPrivileges.includes(PrivilegeNames.IndustryReports);
  const hasAccessToOrganizationReports = userPrivileges.includes(
    PrivilegeNames.OrganizationReports
  );

  const orgReportsURL = hasAccessToOrganizationReports
    ? getUnitSearchForOrgReports(userPrivileges, data?.currentUser?.LicensedOrgUnitId)
    : null;

  const appraisalComparisonURL = userPrivileges.includes(PrivilegeNames.AppraisalComparison)
    ? getUnitSearchForAppraisalComparison(userPrivileges, data?.currentUser?.LicensedOrgUnitId)
    : null;

  const executiveSummaryURL = userPrivileges.includes(
    PrivilegeNames.OrganizationReports_ExecutiveSummary
  )
    ? getExecutiveSummaryUrl(userPrivileges)
    : null;

  return (
    <Drawer open={!menuCollapsed} hideBackdrop data-testid='navigation-menu'>
      <nav
        id='navigationMenu'
        className='nav-menu'
        ref={menuRef}
        aria-label='Site'
        aria-expanded='true'>
        <div className='nav-top'>
          <NavigationMenuCollapse />
          {userName && <div className='welcome-message'>Welcome, {userName}</div>}
        </div>

        <div className='nav-bottom'>
          {userPrivileges.length && (
            <div className='navigation-menu-links scrollBar'>
              {hasAccessToWelcomePage && (
                <NavigationLink to='/data-reports' className='level-0-menu' collapse={false}>
                  Data Reports
                </NavigationLink>
              )}

              {hasAccessToOrganizationReports && (
                <NavigationLink
                  to={getRoleAdjustedUrl('/data-reports/organization-reports', userRoleId)}
                  activeLink='data-reports/organization-reports'
                  className='level-1-menu'
                  collapse={hasAccessToWelcomePage}>
                  Organization Reports
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.OrganizationReports_ExecutiveSummary) && (
                <NavigationLink
                  to={executiveSummaryURL}
                  activeLink='data-reports/organization-reports/executive-summary'
                  className='level-2-menu'>
                  Executive Summary
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.OrganizationReports_UnitReports) && (
                <NavigationLink
                  to={orgReportsURL}
                  activeLink='data-reports/organization-reports/unit-reports'
                  className='level-2-menu'>
                  Unit Report(s)
                </NavigationLink>
              )}

              {hasAccessToIndustryReports && (
                <NavigationLink
                  to={getRoleAdjustedUrl('/data-reports/industry-reports', userRoleId)}
                  className='level-1-menu'
                  collapse={hasAccessToWelcomePage}>
                  Industry Reports
                </NavigationLink>
              )}

              {userRoleId && userPrivileges.includes(PrivilegeNames.BaseLine) && (
                <NavigationLink
                  to={getRoleAdjustedUrl('/data-reports/industry-reports/baseline', userRoleId)}
                  className='level-2-menu'>
                  Baseline
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.AppraisalComparison) && (
                <NavigationLink
                  to={appraisalComparisonURL}
                  activeLink='data-reports/industry-reports/appraisal-comparison'
                  className='level-2-menu'>
                  Appraisal Comparison
                </NavigationLink>
              )}

              {userRoleId && userPrivileges.includes(PrivilegeNames.AppraisalView) && (
                <NavigationLink
                  to={getRoleAdjustedUrl(
                    '/data-reports/industry-reports/customization',
                    userRoleId
                  )}
                  className='level-2-menu'>
                  Appraisal View
                </NavigationLink>
              )}

              {userRoleId && userPrivileges.includes(PrivilegeNames.StrengthsAndChallenges) && (
                <NavigationLink
                  to={getRoleAdjustedUrl(
                    '/data-reports/industry-reports/strengths-challenges',
                    userRoleId
                  )}
                  className='level-2-menu'>
                  Strengths and Challenges
                </NavigationLink>
              )}

              {userRoleId && userPrivileges.includes(PrivilegeNames.AggregatePerformanceReport) && (
                <NavigationLink
                  to={getRoleAdjustedUrl(
                    '/data-reports/industry-reports/aggregate-performance-report',
                    userRoleId
                  )}
                  className='level-2-menu'>
                  Aggregate Performance Report
                </NavigationLink>
              )}

              {userRoleId && userPrivileges.includes(PrivilegeNames.DataCorrelations) && (
                <NavigationLink
                  to={getRoleAdjustedUrl(
                    '/data-reports/industry-reports/data-correlations',
                    userRoleId
                  )}
                  className='level-2-menu'>
                  Data Correlations
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.Resources) && (
                <NavigationLink
                  to='/resources'
                  className='level-0-menu'
                  collapse={
                    hasAccessToIndustryReports ||
                    hasAccessToWelcomePage ||
                    hasAccessToOrganizationReports
                  }>
                  Resources
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.ReleaseNotes) && (
                <NavigationLink to='/release-notes' className='level-0-menu'>
                  Release Notes
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.PostAppraisalSurvey) && (
                <NavigationLink to='/survey' className='level-0-menu'>
                  Appraisal Survey
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.SubmitFeedback) && (
                <NavigationLink to='/submit-feedback' className='level-0-menu'>
                  Submit Feedback
                </NavigationLink>
              )}

              {userPrivileges.includes(PrivilegeNames.ViewFeedback) && <DownloadFeedbackReport />}

              {userPrivileges.includes(PrivilegeNames.DataDownloadPage) && (
                <NavigationLink to='/data-download' className='level-0-menu'>
                  Data Download
                </NavigationLink>
              )}
            </div>
          )}

          <Logo />
        </div>
      </nav>
    </Drawer>
  );
};

export default NavigationMenu;
